import React from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { TaskAssignmentResult } from 'types/interfaces/TaskAssignmentResult';

import { TaskService } from 'components/services/TaskService';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

interface AutoDepartmentAssignmentDialogProps {
  openAutoDepartmentAssignmentDialog: boolean;
  setOpenAutoDepartmentAssignmentDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  selectedTasksToAssign: number[];
  tasksToAssignResponse: TaskAssignmentResult[];
  loadTasks?: () => void | Promise<void>;
}

const errorMessages: Record<string, string> = {
  taskStartError: 'Ошибка начала наряда',
  taskFinishError: 'Ошибка завершения наряда',
  assetError: 'Ошибка объекта',
  assetAddressError: 'Ошибка адреса объекта',
  taskNotFound: 'Наряд не найдена',
  taskTypeError: 'Ошибка типа наряда',
  taskTypeResolutionTimeError: 'Ошибка планового завершения типа наряда',
  taskTypeResponseTimeError: 'Ошибка времени планового начала типа наряда',
  taskTypeProcessingTimeError: 'Ошибка времени обработки типа наряда',
  departmentNotFound: 'Участок не найден',
  planningAlgorithmError: 'В типе наряда не задан алгоритм распределения'
};

const hasErrors = (task: TaskAssignmentResult) =>
  Array.isArray(task.errors) && task.errors.length > 0;

const isSuccessfulDepartmentAssignment = (task: TaskAssignmentResult) =>
  !!task.department && !hasErrors(task);

const AutoDepartmentAssignmentDialog: React.FC<AutoDepartmentAssignmentDialogProps> = ({
  openAutoDepartmentAssignmentDialog,
  setOpenAutoDepartmentAssignmentDialog,
  selectedTasksToAssign,
  tasksToAssignResponse,
  loadTasks
}) => {

  const hasSuccessfulAssignments = tasksToAssignResponse?.some(
    isSuccessfulDepartmentAssignment
  );

  const statusBody = (rowData: TaskAssignmentResult) => {
    const isSuccess = isSuccessfulDepartmentAssignment(rowData);
    const statusText = isSuccess ? 'Распределено' : 'Ошибка';
    const severity = isSuccess ? 'success' : 'danger';
    
    return (
      <Tag 
        value={statusText} 
        severity={severity} 
      />
    );
  };

  const departmentBody = (rowData: TaskAssignmentResult) => {
    if (rowData.department) {
      return <span>{rowData.department.label}</span>;
    }
  };

  const noteBody = (rowData: TaskAssignmentResult) => {
    const errors = Array.isArray(rowData.errors) ? rowData.errors : [];
    if (errors.length === 0) return null;

    return (
      <div>
        {errors.map((error, index) => (
          <div key={index}>
            {errorMessages[error] || error}
          </div>
        ))}
      </div>
    );
  };


  const handleConfirm = async () => { 
    const successfulTaskIds = tasksToAssignResponse
      .map((task, index) =>
        isSuccessfulDepartmentAssignment(task)
          ? task.taskId ?? selectedTasksToAssign[index]
          : null
      )
      .filter((taskId): taskId is number => typeof taskId === 'number' && !isNaN(taskId));
    
    if (successfulTaskIds.length > 0) {
      try {
        await TaskService.AutoTaskAssignment(successfulTaskIds, false);
        await loadTasks?.();
      } catch (error) {
        console.error('Error confirming auto assignment:', error);
      } finally {
        setOpenAutoDepartmentAssignmentDialog(false);
      }
    } else {
      setOpenAutoDepartmentAssignmentDialog(false);
    }
  };

  const renderFooter = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <Button 
          label="Подтвердить" 
          onClick={handleConfirm}
          disabled={!hasSuccessfulAssignments}
          severity={!hasSuccessfulAssignments ? 'secondary' : undefined}
        />
        <Button 
          label="Внести корректировки" 
          onClick={() => setOpenAutoDepartmentAssignmentDialog(false)}
        />
      </div>
    );
  };

  return (
    <Dialog
      header="Результаты автораспределения"
      visible={openAutoDepartmentAssignmentDialog}
      style={{ width: '95vw', minWidth: 800, minHeight: 300 }}
      onHide={() => setOpenAutoDepartmentAssignmentDialog(false)}
      footer={renderFooter}
      pt={withDialogFooterSpacing()}
    >
      <DataTable
        size="small"
        value={tasksToAssignResponse}
        lazy
        scrollable
        emptyMessage="Нет данных"
      >
        <Column
          field="taskId"
          header="Номер наряда"
          style={{ width: '14%' }}
        />
        <Column
          field="taskType"
          header="Тип наряда"
          body={(rowData: TaskAssignmentResult) => rowData.taskType?.label || ''}
          style={{ width: '14%' }}
        />
        <Column
          field="name"
          header="Название наряда"
          style={{ width: '14%' }}
        />
        <Column
          header="Статус распределения"
          body={statusBody}
          style={{ width: '14%' }}
        />
        <Column
          header="Участок"
          body={departmentBody}
          style={{ width: '14%' }}
        />
        <Column
          header="Примечание"
          body={noteBody}
          style={{ width: '30%' }}
        />
      </DataTable>
    </Dialog>
  );
};

export default AutoDepartmentAssignmentDialog;
